import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../../api";
import urls from "../../../urls";
import type { DataValue } from "../../DataSheet/DataSheet";
import { mapLabelToMeasurand } from "../mapLabelToMeasurand";

/** Maps a counter label (e.g. "Verbrauch/Entnahme") to its changed values,
 * keyed by ISO-ish date string. Matches `CounterDataView`'s `onSetRequestData`. */
type CounterUpdateRequestData = Record<string, Record<string, DataValue>>;

export function useMeterEnergyDataMutation(meterId: number | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (counterUpdateRequestData: CounterUpdateRequestData) =>
      patchMeterEnergyData(meterId, counterUpdateRequestData),
    onSuccess() {
      if (meterId === undefined) {
        return;
      }

      queryClient.invalidateQueries({
        queryKey: [
          "counterDataViewData",
          { dataUrl: urls.api.energyDataRawData(meterId, false) }
        ]
      });
    }
  });
}

function patchMeterEnergyData(
  meterId: number | undefined,
  counterUpdateRequestData: CounterUpdateRequestData
) {
  if (meterId === undefined) {
    return Promise.resolve([]);
  }

  const promises = Object.keys(counterUpdateRequestData).map((counterLabel) =>
    api.patch(urls.api.meterEnergyData(), {
      meterId,
      measurand: mapLabelToMeasurand(counterLabel),
      energyData: counterUpdateRequestData[counterLabel]
    })
  );

  return Promise.all(promises);
}

export { CounterUpdateRequestData };
